import { useState, type ReactNode } from 'react'
import { PopoverSheet } from '@/components/ui/PopoverSheet'
import { useChangeRowCategory, useChangeRowProject } from '@/lib/time-queries'
import { rowLabel } from '@/lib/timesheet-copy'
import type { PickerData, WeekRow } from '@/lib/types'
import { cn } from '@/lib/utils'
import { EntryPicker } from './EntryPicker'
import type { MenuStep, NewRowSelection } from './week-rows'

/** Op welk deel van het label geklikt is; dat bepaalt waar de kiezer opent. */
export type TargetLevel = 'client' | 'project' | 'category'

interface RowTargetMenuProps {
  row: WeekRow
  pickers: PickerData
  level: TargetLevel
  /** Alle entries van deze regel in de getoonde week; die verhuizen samen. */
  entryIds: string[]
  weekId: string
  personId?: string
  /** Op een regel zonder opgeslagen uren is de wissel een lokale vervanging, geen API-call. */
  onLocalCommit?: (selection: NewRowSelection) => void
  /** Vervangt de standaard tekstknop, bijvoorbeeld door een icoonknop op de mobiele kaart. */
  trigger?: ReactNode
}

function startStep(level: TargetLevel, row: WeekRow, pickers: PickerData): MenuStep {
  if (level === 'client') return { kind: 'client' }
  if (level === 'project') return { kind: 'project', clientId: row.clientId, clientName: row.clientName }
  const project = pickers.projects.find((p) => p.id === row.projectId)
  return project
    ? { kind: 'category', project }
    : { kind: 'project', clientId: row.clientId, clientName: row.clientName }
}

/**
 * De bestemmingskiezer achter alle drie de klikdoelen in het regellabel. Hij opent op het
 * niveau dat je aanklikte en loopt van daaraf naar beneden; de lijst zelf is dezelfde
 * `EntryPicker` als bij het toevoegen van een regel.
 *
 * Wat er niet in zit is een weg omhoog vanaf het project: het projectmenu toont bewust
 * alleen de eigen klant, want anders doen twee klikdoelen hetzelfde en blijft de oude
 * gewoonte bestaan. Van klant wisselen gaat via de klantnaam.
 */
export function RowTargetMenu({ row, pickers, level, entryIds, weekId, personId, onLocalCommit, trigger }: RowTargetMenuProps) {
  const [open, setOpen] = useState(false)
  const changeProject = useChangeRowProject(weekId, personId)
  const changeCategory = useChangeRowCategory(weekId, personId)
  const pending = changeProject.isPending || changeCategory.isPending

  function commit(selection: NewRowSelection) {
    // De wissel is een PATCH-lus over alle entries van de regel. Een tweede klik
    // start een overlappende lus die weer bij maandag begint, en dan staat de ene
    // helft van de week op het oude en de andere op het nieuwe project.
    if (pending) return
    if (selection.projectId === row.projectId && selection.categoryId === row.categoryId) {
      setOpen(false)
      return
    }
    if (onLocalCommit) {
      onLocalCommit(selection)
      setOpen(false)
      return
    }
    // Alleen de categorie veranderd: dan hoeft het project niet mee de PATCH in, en
    // leest de melding achteraf ook als een categoriewissel.
    if (selection.projectId === row.projectId) {
      changeCategory.mutate({ entryIds, categoryId: selection.categoryId }, { onSuccess: () => setOpen(false) })
      return
    }
    changeProject.mutate(
      {
        entryIds,
        projectId: selection.projectId,
        categoryId: selection.categoryId,
        label: rowLabel(selection.clientName, selection.projectName, selection.categoryName),
      },
      { onSuccess: () => setOpen(false) },
    )
  }

  const defaultTrigger = {
    client: { label: `Klant wijzigen: ${row.clientName}`, text: row.clientName },
    project: { label: `Project wijzigen: ${row.projectName}`, text: row.projectName },
    category: {
      label: `Categorie wijzigen: ${row.categoryName ?? 'geen categorie'}`,
      text: row.categoryName ?? 'Categorie kiezen',
    },
  }[level]

  return (
    <PopoverSheet
      open={open}
      onOpenChange={setOpen}
      title={defaultTrigger.label}
      className="w-72"
      trigger={
        trigger ?? (
          <button
            type="button"
            className={cn(
              'inline-flex min-w-0 items-center rounded-sm px-1 transition-colors hover:bg-fill-1 hover:text-ink',
              level === 'client' ? 'font-medium text-ink' : 'text-ink-faint',
            )}
            aria-label={defaultTrigger.label}
          >
            <span className="truncate">{defaultTrigger.text}</span>
          </button>
        )
      }
    >
      <EntryPicker
        pickers={pickers}
        start={startStep(level, row, pickers)}
        current={{ clientId: row.clientId, projectId: row.projectId, categoryId: row.categoryId }}
        pending={pending}
        onPick={commit}
      />
    </PopoverSheet>
  )
}
